import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getTrainers, Trainer } from "../../services/trainerService";
import {
  updateWeekWorkoutProgram,
  WeekWorkoutProgram,
  WeekWorkoutProgramFormPayload as Payload,
  WeekWorkoutProgramFormPayload,
  createWeekWorkoutProgram,
} from "../../services/weekWorkoutService";
import api from "../../services/api";
import Select from "react-select";
import { debounce } from "lodash";
import {
  getExercises,
  searchExercisesId,
} from "../../services/exerciseService";
import {
  getHowToPerforms,
  searchHTP,
} from "../../services/howToPerformService";

// -----------------------------
// Interfaces & Types
// -----------------------------
interface FilterOption {
  label: string;
  value: number;
  type: "exercise" | "tutorial";
}

interface WorkoutExercise {
  exerciseId?: number;
  name: string;
  htpId?: number;
}

interface Exercise {
  name: string;
  description?: string;
  exercise?: WorkoutExercise;
  searchType?: "exercise" | "tutorial";
}

interface MultiSet {
  exercises: Exercise[];
  type: "superset" | "triset" | "dropset";
  restTimeSec?: number;
}

interface WorkoutDay {
  id?: number;
  dayNumber: number;
  title?: string;
  duration?: number;
  warmup: (Exercise | MultiSet)[];
  mainWorkout: (Exercise | MultiSet)[];
  cooldown: (Exercise | MultiSet)[];
  workoutFinisher: (Exercise | MultiSet)[];
  tips: string;
}

interface WorkoutTrackForm {
  type: "Strength" | "StrengthConditioning" | "Home";
  trainerId?: number;
  days: WorkoutDay[];
  photoUrl?: string;
}

interface WeekWorkoutProgramForm {
  title: string;
  description: string;
  tracks: WorkoutTrackForm[];
}

interface Completion {
  id: number;
  user: { id: number; name: string };
  completedAt: string;
}

// -----------------------------
// Helper Functions
// -----------------------------
const getFormikError = (
  errors: any,
  touched: any,
  path: (string | number)[],
) => {
  let e = errors;
  let t = touched;
  for (const key of path) {
    e = e?.[key];
    t = t?.[key];
  }
  return t && typeof e === "string" ? e : null;
};

const isMultiSet = (item: Exercise | MultiSet): item is MultiSet => {
  return "type" in item;
};

const DAY_SECTIONS = [
  "warmup",
  "mainWorkout",
  "workoutFinisher",
  "cooldown",
] as const;

// -----------------------------
// Validation Schema
// -----------------------------
const exerciseOrMultiSetSchema = Yup.lazy((value: any) => {
  if (
    value?.type === "superset" ||
    value?.type === "tripset" ||
    value?.type === "triset" ||
    value?.type === "dropset"
  ) {
    return Yup.object({
      type: Yup.string().required(),
      exercises: Yup.array()
        .of(
          Yup.object({
            name: Yup.string().required(
              "Exercise name is muli set is required",
            ),
            description: Yup.string().optional(),
          }),
        )
        .min(
          value.type === "dropset" ? 1 : value.type === "superset" ? 2 : 3,
          value.type === "superset"
            ? "Superset must have at least 2 exercises"
            : "Tri-set must have at least 3 exercises",
        )
        .required(),
    });
  }

  return Yup.object({
    name: Yup.string().required("Exercise name is required"),
    description: Yup.string().optional(),
  });
});

const workoutSectionSchema = Yup.array().of(exerciseOrMultiSetSchema);

const validationSchema = Yup.object({
  title: Yup.string().required("Required"),
  tracks: Yup.array()
    .of(
      Yup.object({
        trainerId: Yup.number().nullable(),
        days: Yup.array().of(
          Yup.object({
            dayNumber: Yup.number(),
            title: Yup.string().nullable(),
            duration: Yup.number().nullable(),
            warmup: workoutSectionSchema,
            mainWorkout: workoutSectionSchema,
            workoutFinisher: workoutSectionSchema,
            cooldown: workoutSectionSchema,
            tips: Yup.string(),
          })
            .test(
              "at-least-one-exercise",
              "Day must contain at least one exercise",
              (day) =>
                DAY_SECTIONS.some(
                  (section) => (day?.[section]?.length ?? 0) > 0,
                ),
            )
            .test(
              "To-long-day-title",
              "Day title must contain < 50 letters",
              (day) => !day?.title || day.title.length < 50,
            ),
        ),
      }).test(
        "trainer-required-if-days",
        "If this track contains days, a trainer must be selected",
        function (track) {
          if (track.days && track.days.length > 0 && !track.trainerId) {
            return this.createError({
              path: `${this.path}.trainerId`,
              message:
                "If this track contains days, a trainer must be selected",
            });
          } else if (track.trainerId && track.days && track.days.length === 0) {
            return this.createError({
              path: `${this.path}.trainerId`,
              message: "This track doesn't have any days",
            });
          }
          return true;
        },
      ),
    )
    .test(
      "at-least-one-track",
      "At least one track with a trainer and at least one day is required",
      (tracks) =>
        tracks?.some((t) => t.trainerId && t.days && t.days?.length > 0),
    ),
});

// -----------------------------
// Constants
// -----------------------------
const emptyTracks: WeekWorkoutProgramForm["tracks"] = [
  { type: "Strength", trainerId: undefined, days: [], photoUrl: "" },
  {
    type: "StrengthConditioning",
    trainerId: undefined,
    days: [],
    photoUrl: "",
  },
  { type: "Home", trainerId: undefined, days: [], photoUrl: "" },
];

const trackColorMap = {
  Home: "#E0F2F0",
  Strength: "#FEE2EF",
  StrengthConditioning: "#FEF3CF",
};

// -----------------------------
// Component
// -----------------------------
const EditWeekWorkoutProgram: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const programId = (location.state as any)?.id as number;

  const [nameOptions, setNameOptions] = useState<FilterOption[]>([]);
  const [nameTutorialOptions, setNameTutorialOptions] = useState<
    FilterOption[]
  >([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [initialValues, setInitialValues] =
    useState<WeekWorkoutProgramForm | null>(null);

  // -----------------------------
  // Debounced search for exercises
  // -----------------------------
  const debouncedNameSearch = debounce(async (inputValue: string) => {
    try {
      if (inputValue && inputValue.length > 0) {
        const results = await searchExercisesId("name", inputValue);
        setNameOptions(
          results.map((ex) => ({
            label: ex.name,
            value: ex.id,
            type: "exercise" as const,
          })),
        );
      } else {
        getAllExercises();
      }
    } catch (error) {
      console.error("Error searching exercises:", error);
    }
  }, 300);

  // -----------------------------
  // Get all
  // -----------------------------
  const getAllExercises = async () => {
    try {
      const exercises = await getExercises(1, 1000);
      if (exercises.data) {
        setNameOptions(
          exercises.data.map((ex) => ({
            label: ex.name,
            value: ex.id,
            type: "exercise" as const,
          })),
        );
      }
    } catch (error) {
      console.error("Error searching names:", error);
    }
  };

  // -----------------------------
  // Get all Tutorials
  // -----------------------------
  const getAllTutorials = async () => {
    try {
      const tutorials = await getHowToPerforms();
      if (tutorials.data) {
        setNameTutorialOptions(
          tutorials.data.map((t) => ({
            label: t.name,
            value: t.id,
            type: "tutorial" as const,
          })),
        );
      }
    } catch (error) {
      console.error("Error searching names:", error);
    }
  };

  // -----------------------------
  // Debounced search for tutorials
  // -----------------------------
  const debouncedTutorialSearch = debounce(async (inputValue: string) => {
    try {
      if (inputValue && inputValue.length > 0) {
        const results = await searchHTP("name", inputValue);
        setNameTutorialOptions(
          results.map((t) => ({
            label: t.name,
            value: t.id,
            type: "tutorial" as const,
          })),
        );
      } else {
        getAllTutorials();
      }
    } catch (error) {
      console.error("Error searching tutorials:", error);
    }
  }, 300);

  // -----------------------------
  // Load trainers
  // -----------------------------
  useEffect(() => {
    getAllExercises();
    getAllTutorials();
    getTrainers()
      .then(setTrainers)
      .catch((err) => console.error(err));
  }, []);

  // -----------------------------
  // Normalization helpers
  // -----------------------------
  const normalizeExercise = useCallback(
    (item: any): Exercise | MultiSet | null => {
      if (!item) return null;

      // MULTISET
      if (item?.type && Array.isArray(item.exercises)) {
        const exercises: Exercise[] = item.exercises
          .filter((ex: any) => ex?.name || ex?.exercise?.name)
          .map((ex: any) => {
            const baseExercise = ex.exercise || {};
            return {
              name: ex.name?.trim() || baseExercise.name?.trim() || "",
              description: ex.description || "",
              searchType: ex.searchType,
              exercise: {
                name: baseExercise.name || ex.name || "",
                exerciseId: baseExercise.exerciseId || ex.id || undefined,
                htpId: baseExercise.htpId,
              },
            };
          });

        if (exercises.length === 0) return null;

        return {
          type: item.type,
          restTimeSec: item.restTimeSec ?? 0,
          exercises,
        };
      }

      // SINGLE EXERCISE
      if (item?.name || item?.exercise?.name) {
        const baseExercise = item.exercise || {};
        return {
          name: item.name?.trim() || baseExercise.name?.trim() || "",
          description: item.description || "",
          searchType: item.searchType,
          exercise: {
            name: baseExercise.name || item.name || "",
            exerciseId: baseExercise.exerciseId || item.id || undefined,
            htpId: baseExercise.htpId,
          },
        };
      }

      return null;
    },
    [],
  );

  const normalizeDayExercises = useCallback(
    (day: any): WorkoutDay => ({
      ...day,
      warmup: (day.warmup || [])
        .map(normalizeExercise)
        .filter(
          (ex: Exercise | MultiSet | null): ex is Exercise | MultiSet =>
            ex !== null,
        ),
      mainWorkout: (day.mainWorkout || [])
        .map(normalizeExercise)
        .filter(
          (ex: Exercise | MultiSet | null): ex is Exercise | MultiSet =>
            ex !== null,
        ),
      workoutFinisher: (day.workoutFinisher || [])
        .map(normalizeExercise)
        .filter(
          (ex: Exercise | MultiSet | null): ex is Exercise | MultiSet =>
            ex !== null,
        ),
      cooldown: (day.cooldown || [])
        .map(normalizeExercise)
        .filter(
          (ex: Exercise | MultiSet | null): ex is Exercise | MultiSet =>
            ex !== null,
        ),
    }),
    [normalizeExercise],
  );

  const normalizeDaysForPayload = (days: any[]) =>
    days.map((day) => ({
      ...day,
      duration:
        day.duration === "" || day.duration === undefined
          ? null
          : Number(day.duration),
    }));

  // -----------------------------
  // Load program data
  // -----------------------------
  useEffect(() => {
    if (!programId) return;

    const fetchProgram = async () => {
      try {
        const response = await api.get<WeekWorkoutProgram>(
          `/week-workouts/${programId}`,
        );

        const program = response.data;

        const programTracks: WeekWorkoutProgramForm["tracks"] =
          program.tracks.map((track) => ({
            type: track.type,
            trainerId: track.trainer?.id,
            photoUrl: track.trainer?.photoUrl,
            days: (track.days || []).map(normalizeDayExercises),
          }));

        const missingTracks = emptyTracks.filter(
          (t) => !programTracks.find((pt) => pt.type === t.type),
        );

        console.log("programTracks", programTracks);

        setInitialValues({
          title: program.title,
          description: program.description || "",
          tracks: [...programTracks, ...missingTracks],
        });
      } catch (err) {
        console.error(err);
      }
    };

    fetchProgram();
  }, [programId, normalizeDayExercises]);

  if (!initialValues) return <div>Loading...</div>;

  // -----------------------------
  // Normalize days for payload
  // -----------------------------
  const normalizeDays = (days: any[]) =>
    days.map((day) => ({
      ...day,
      duration:
        day.duration === "" || day.duration === undefined
          ? null
          : Number(day.duration),
    }));

  // -----------------------------
  // Submit handlers
  // -----------------------------
  const handleSubmit = async (values: WeekWorkoutProgramForm) => {
    try {
      const normalizedValues: WeekWorkoutProgramForm = {
        ...values,
        tracks: values.tracks.map((track) => ({
          ...track,
          days: track.days.map(normalizeDayExercises),
        })),
      };

      try {
        await validationSchema.validate(normalizedValues, {
          abortEarly: false,
        });
      } catch (err: any) {
        console.log("Validation Error:");
        err.inner.forEach((e: any) => {
          console.log(`Field: ${e.path || "(unknown)"}, Message: ${e.message}`);
        });
      }

      const payload: Payload = {
        title: normalizedValues.title,
        description: normalizedValues.description,
        tracks: normalizedValues.tracks
          .filter((t) => t.trainerId != null)
          .map((t) => ({
            type: t.type,
            trainerId: t.trainerId!,
            days: normalizeDaysForPayload(t.days),
          })),
      };

      await updateWeekWorkoutProgram(programId, payload);
      alert("Program updated successfully");
      navigate("/week-program");
    } catch (err) {
      console.error(err);
      alert("Failed to update program");
    }
  };

  const handleCreateAsNew = async (values: WeekWorkoutProgramForm) => {
    try {
      const normalizedValues: WeekWorkoutProgramForm = {
        ...values,
        tracks: values.tracks.map((track) => ({
          ...track,
          days: track.days.map(normalizeDayExercises),
        })),
      };

      try {
        await validationSchema.validate(normalizedValues, {
          abortEarly: false,
        });
      } catch (err: any) {
        console.log("Validation Error:");
        err.inner.forEach((e: any) => {
          console.log(`Field: ${e.path || "(unknown)"}, Mess: ${e.message}`);
        });
        return;
      }
      const payload: WeekWorkoutProgramFormPayload = {
        title: normalizedValues.title,
        description: normalizedValues.description,
        tracks: normalizedValues.tracks
          .filter((t) => t.trainerId)
          .map((t) => ({
            type: t.type,
            trainerId: t.trainerId!,
            days: normalizeDays(t.days),
          })),
      };

      await createWeekWorkoutProgram(payload);
      alert("Program created successfully");
      navigate("/week-program");
    } catch (err) {
      console.error("Failed to create program", err);
      alert("Failed to create program");
    }
  };

  // -----------------------------
  // Completion Table Component
  // -----------------------------
  interface CompletionRow {
    id: number;
    userName: string;
    completedAt: string;
  }

  const CompletionTable: React.FC<{ dayId: number | null }> = ({ dayId }) => {
    const [completions, setCompletions] = useState<CompletionRow[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchCompletions = async () => {
        try {
          const res = await api.get<Completion[]>(
            `/week-workouts/${dayId}/completions`,
          );
          setCompletions(
            res.data.map((c) => ({
              id: c.id,
              userName: c.user.name,
              completedAt: new Date(c.completedAt).toLocaleString(),
            })),
          );
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchCompletions();
    }, [dayId]);

    if (loading) return <div>Loading completions...</div>;
    if (completions.length === 0) return <div>No completions yet.</div>;

    return <div>Completed {completions.length}</div>;
  };

  // -----------------------------
  // Render Formik Form
  // -----------------------------
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: 40,
      height: 40,
      borderWidth: 1,
      borderColor: "#gray",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 8px",
      borderWidth: 0,
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: 40,
    }),
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Edit Trainer Program</h1>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, setFieldTouched }) => (
          <Form className="space-y-6">
            {/* Program Info */}
            <div className="bg-white p-6 rounded shadow space-y-4">
              <div>
                <label className="block font-semibold mb-1">
                  Program Title
                </label>
                <Field
                  name="title"
                  placeholder="Enter program title"
                  className="w-full border rounded p-2"
                />
                {errors.title && touched.title && (
                  <div className="text-red-500 text-sm">{errors.title}</div>
                )}
              </div>

              <div>
                <label className="block font-semibold mb-1">Description</label>
                <ReactQuill
                  value={values.description}
                  onChange={(content) => setFieldValue("description", content)}
                  className="bg-white border rounded"
                />
              </div>
            </div>

            {/* Tracks */}
            <FieldArray name="tracks">
              {() => (
                <>
                  {values.tracks.map((track, trackIndex) => (
                    <div
                      key={trackIndex}
                      style={{ backgroundColor: trackColorMap[track.type] }}
                      className="bg-white p-6 rounded shadow space-y-4"
                    >
                      {/* Track Header */}
                      <h2 className="text-xl font-bold">{track.type} Track</h2>

                      {/* Trainer Select */}
                      <div className="flex items-center gap-4 mb-4">
                        <select
                          className="border p-2 rounded w-full"
                          value={track.trainerId || ""}
                          onChange={(e) =>
                            setFieldValue(
                              `tracks[${trackIndex}].trainerId`,
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                        >
                          <option value="">Select Trainer</option>
                          {trainers.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Days */}
                      <FieldArray name={`tracks[${trackIndex}].days`}>
                        {({ push: pushDay, remove: removeDay }) => (
                          <div className="space-y-4">
                            {/* Render each day */}
                            {track.days
                              .sort((a, b) => a.dayNumber - b.dayNumber)
                              .map((day, dayIndex) => {
                                const dayError = getFormikError(
                                  errors,
                                  touched,
                                  ["tracks", trackIndex, "days", dayIndex],
                                );

                                return (
                                  <div
                                    key={dayIndex}
                                    className="border rounded p-2 bg-gray-50 space-y-3"
                                  >
                                    <CompletionTable dayId={day?.id ?? null} />
                                    {dayError && (
                                      <div className="text-red-500 text-sm mt-1">
                                        {dayError}
                                      </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                      <h3 className="text-l font-bold">
                                        Day {day.dayNumber || dayIndex + 1}
                                      </h3>
                                      <button
                                        type="button"
                                        onClick={() => removeDay(dayIndex)}
                                        className="text-red-500"
                                      >
                                        Remove Day
                                      </button>
                                    </div>
                                    {/* Day Title & Duration */}
                                    <div className="flex gap-4 mb-2">
                                      <Field
                                        name={`tracks[${trackIndex}].days[${dayIndex}].title`}
                                        placeholder="Day Title"
                                        className="border p-1 rounded w-full"
                                      />
                                      <Field
                                        name={`tracks[${trackIndex}].days[${dayIndex}].duration`}
                                        placeholder="Duration (min)"
                                        type="number"
                                        className="border p-1 rounded w-32"
                                      />
                                    </div>
                                    {/* Exercises */}
                                    {(
                                      [
                                        "warmup",
                                        "mainWorkout",
                                        "workoutFinisher",
                                        "cooldown",
                                      ] as const
                                    ).map((section) => (
                                      <FieldArray
                                        key={section}
                                        name={`tracks[${trackIndex}].days[${dayIndex}].${section}`}
                                      >
                                        {({
                                          push: pushEx,
                                          remove: removeEx,
                                        }) => (
                                          <div className="space-y-1 p-2">
                                            <h4 className="font-semibold text-l">
                                              {section}
                                            </h4>

                                            {/* Render each exercise or multisets */}
                                            {(
                                              (day[section] ?? []) as (
                                                | Exercise
                                                | MultiSet
                                              )[]
                                            ).map((item, exIndex) => {
                                              // -----------------------------
                                              // MULTISET
                                              // -----------------------------
                                              if (isMultiSet(item)) {
                                                return (
                                                  <div
                                                    key={exIndex}
                                                    className="border p-1 rounded space-y-1"
                                                  >
                                                    <div className="flex justify-between items-center">
                                                      <h5 className="text-xs font-semibold">
                                                        {item.type.toUpperCase()}
                                                      </h5>
                                                      <button
                                                        type="button"
                                                        className="text-red-500"
                                                        onClick={() =>
                                                          removeEx(exIndex)
                                                        }
                                                      >
                                                        Remove
                                                      </button>
                                                    </div>

                                                    {/* Render exercises inside multisets */}
                                                    {item.exercises.map(
                                                      (ex, idx) => {
                                                        const innerNameError =
                                                          getFormikError(
                                                            errors,
                                                            touched,
                                                            [
                                                              "tracks",
                                                              trackIndex,
                                                              "days",
                                                              dayIndex,
                                                              section,
                                                              exIndex,
                                                              "exercises",
                                                              idx,
                                                              "name",
                                                            ],
                                                          );

                                                        return (
                                                          <div key={idx}>
                                                            {/* Search Type Buttons */}
                                                            <div className="flex gap-2 mb-1">
                                                              <button
                                                                type="button"
                                                                className={`px-1 py-1 rounded text-sm ${
                                                                  ex.searchType ===
                                                                  "exercise"
                                                                    ? "bg-blue-500 text-white"
                                                                    : "bg-gray-200"
                                                                }`}
                                                                onClick={() => {
                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].exercise`,
                                                                    {
                                                                      name: "",
                                                                      exerciseId:
                                                                        undefined,
                                                                      htpId:
                                                                        undefined,
                                                                    },
                                                                  );
                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].name`,
                                                                    "",
                                                                  );

                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].searchType`,
                                                                    "exercise",
                                                                  );
                                                                }}
                                                              >
                                                                Exercise
                                                              </button>
                                                              <button
                                                                type="button"
                                                                className={`px-1 py-1 rounded text-sm ${
                                                                  ex.searchType ===
                                                                  "tutorial"
                                                                    ? "bg-green-500 text-white"
                                                                    : "bg-gray-200"
                                                                }`}
                                                                onClick={() => {
                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].exercise`,
                                                                    {
                                                                      name: "",
                                                                      exerciseId:
                                                                        undefined,
                                                                      htpId:
                                                                        undefined,
                                                                    },
                                                                  );
                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].name`,
                                                                    "",
                                                                  );
                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].searchType`,
                                                                    "tutorial",
                                                                  );
                                                                }}
                                                              >
                                                                Tutorial
                                                              </button>
                                                            </div>
                                                            <div className="flex gap-2 items-center">
                                                              <Select
                                                                isMulti={false}
                                                                className="basic-select  w-full"
                                                                styles={
                                                                  selectStyles
                                                                }
                                                                isDisabled={
                                                                  ex.searchType !==
                                                                    "exercise" &&
                                                                  ex.searchType !==
                                                                    "tutorial"
                                                                }
                                                                classNamePrefix="select"
                                                                placeholder="Enter exercise name"
                                                                options={
                                                                  ex.searchType ===
                                                                  "exercise"
                                                                    ? nameOptions
                                                                    : nameTutorialOptions
                                                                }
                                                                value={
                                                                  ex.searchType ===
                                                                    "exercise" &&
                                                                  ex.exercise
                                                                    ?.exerciseId
                                                                    ? {
                                                                        label:
                                                                          ex
                                                                            .exercise
                                                                            .name,
                                                                        value:
                                                                          ex
                                                                            .exercise
                                                                            ?.exerciseId,
                                                                      }
                                                                    : ex.searchType ===
                                                                          "tutorial" &&
                                                                        ex
                                                                          .exercise
                                                                          ?.htpId
                                                                      ? {
                                                                          label:
                                                                            ex
                                                                              .exercise
                                                                              .name,
                                                                          value:
                                                                            ex
                                                                              .exercise
                                                                              ?.htpId,
                                                                        }
                                                                      : null
                                                                }
                                                                onChange={(
                                                                  selected,
                                                                ) => {
                                                                  if (
                                                                    !selected
                                                                  ) {
                                                                    setFieldValue(
                                                                      `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].exercise`,
                                                                      {
                                                                        name: "",
                                                                        exerciseId:
                                                                          undefined,
                                                                        htpId:
                                                                          undefined,
                                                                      },
                                                                    );
                                                                    return;
                                                                  }

                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].exercise`,
                                                                    {
                                                                      name: selected.label,
                                                                      exerciseId:
                                                                        ex.searchType ===
                                                                        "exercise"
                                                                          ? selected.value
                                                                          : undefined,
                                                                      htpId:
                                                                        ex.searchType ===
                                                                        "tutorial"
                                                                          ? selected.value
                                                                          : undefined,
                                                                    },
                                                                  );

                                                                  setFieldValue(
                                                                    `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].name`,
                                                                    selected.label,
                                                                  );
                                                                }}
                                                                onInputChange={
                                                                  ex.searchType ===
                                                                  "exercise"
                                                                    ? debouncedNameSearch
                                                                    : debouncedTutorialSearch
                                                                }
                                                                isClearable
                                                                filterOption={(
                                                                  option,
                                                                  inputValue,
                                                                ) => {
                                                                  const label =
                                                                    typeof option.label ===
                                                                    "string"
                                                                      ? option.label
                                                                      : "";
                                                                  const input =
                                                                    typeof inputValue ===
                                                                    "string"
                                                                      ? inputValue
                                                                      : "";
                                                                  return label
                                                                    .toLowerCase()
                                                                    .includes(
                                                                      input.toLowerCase(),
                                                                    );
                                                                }}
                                                              />
                                                              <Field
                                                                name={`tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].name`}
                                                                placeholder="Exercise name"
                                                                className="border p-1 rounded w-full h-10"
                                                              />
                                                              <Field
                                                                name={`tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercises[${idx}].description`}
                                                                placeholder="Description"
                                                                className="border p-1 rounded w-full h-10"
                                                              />
                                                            </div>
                                                            {innerNameError && (
                                                              <div className="text-red-500 text-xs mt-1">
                                                                {innerNameError}
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      },
                                                    )}
                                                  </div>
                                                );
                                              }

                                              // -----------------------------
                                              // SINGLE EXERCISE
                                              // -----------------------------
                                              const exerciseNameError =
                                                getFormikError(
                                                  errors,
                                                  touched,
                                                  [
                                                    "tracks",
                                                    trackIndex,
                                                    "days",
                                                    dayIndex,
                                                    section,
                                                    exIndex,
                                                    "name",
                                                  ],
                                                );

                                              return (
                                                <div
                                                  key={exIndex}
                                                  className="border p-1 rounded"
                                                >
                                                  <div className="flex justify-between items-center">
                                                    <h5 className="text-xs font-semibold">
                                                      {"exercise".toUpperCase()}
                                                    </h5>
                                                    <button
                                                      type="button"
                                                      className="text-red-500"
                                                      onClick={() =>
                                                        removeEx(exIndex)
                                                      }
                                                    >
                                                      Remove
                                                    </button>
                                                  </div>
                                                  {/* Search Type Buttons */}
                                                  <div className="flex gap-2 mb-1">
                                                    <button
                                                      type="button"
                                                      className={`px-1 py-1 rounded text-sm ${
                                                        item.searchType ===
                                                        "exercise"
                                                          ? "bg-blue-500 text-white"
                                                          : "bg-gray-200"
                                                      }`}
                                                      onClick={() => {
                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercise`,
                                                          {
                                                            name: "",
                                                            exerciseId:
                                                              undefined,
                                                            htpId: undefined,
                                                          },
                                                        );
                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].name`,
                                                          "",
                                                        );

                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].searchType`,
                                                          "exercise",
                                                        );
                                                      }}
                                                    >
                                                      Exercise
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className={`px-1 py-1 rounded text-sm ${
                                                        item.searchType ===
                                                        "tutorial"
                                                          ? "bg-green-500 text-white"
                                                          : "bg-gray-200"
                                                      }`}
                                                      onClick={() => {
                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercise`,
                                                          {
                                                            name: "",
                                                            exerciseId:
                                                              undefined,
                                                            htpId: undefined,
                                                          },
                                                        );
                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].name`,
                                                          "",
                                                        );
                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].searchType`,
                                                          "tutorial",
                                                        );
                                                      }}
                                                    >
                                                      Tutorial
                                                    </button>
                                                  </div>
                                                  <div className="flex gap-2 items-center">
                                                    <Select
                                                      isMulti={false}
                                                      className="basic-select w-full"
                                                      isDisabled={
                                                        item.searchType !==
                                                          "exercise" &&
                                                        item.searchType !==
                                                          "tutorial"
                                                      }
                                                      styles={selectStyles}
                                                      placeholder="Enter exercise name"
                                                      classNamePrefix="select"
                                                      options={
                                                        item.searchType ===
                                                        "exercise"
                                                          ? nameOptions
                                                          : nameTutorialOptions
                                                      }
                                                      value={
                                                        item.searchType ===
                                                          "exercise" &&
                                                        item.exercise
                                                          ?.exerciseId
                                                          ? {
                                                              label:
                                                                item.exercise
                                                                  .name,
                                                              value:
                                                                item.exercise
                                                                  .exerciseId,
                                                            }
                                                          : item.searchType ===
                                                                "tutorial" &&
                                                              item.exercise
                                                                ?.htpId
                                                            ? {
                                                                label:
                                                                  item.exercise
                                                                    .name,
                                                                value:
                                                                  item.exercise
                                                                    .htpId,
                                                              }
                                                            : null
                                                      }
                                                      onChange={(selected) => {
                                                        if (!selected) {
                                                          setFieldValue(
                                                            `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercise`,
                                                            undefined,
                                                          );
                                                          return;
                                                        }

                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].exercise`,
                                                          {
                                                            name: selected.label,
                                                            exerciseId:
                                                              item.searchType ===
                                                              "exercise"
                                                                ? selected.value
                                                                : undefined,
                                                            htpId:
                                                              item.searchType ===
                                                              "tutorial"
                                                                ? selected.value
                                                                : undefined,
                                                          },
                                                        );
                                                        setFieldValue(
                                                          `tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].name`,
                                                          selected.label,
                                                        );
                                                      }}
                                                      onInputChange={
                                                        item.searchType ===
                                                        "exercise"
                                                          ? debouncedNameSearch
                                                          : debouncedTutorialSearch
                                                      }
                                                      isClearable
                                                      filterOption={(
                                                        option,
                                                        inputValue,
                                                      ) => {
                                                        const label =
                                                          typeof option.label ===
                                                          "string"
                                                            ? option.label
                                                            : "";
                                                        const input =
                                                          typeof inputValue ===
                                                          "string"
                                                            ? inputValue
                                                            : "";
                                                        return label
                                                          .toLowerCase()
                                                          .includes(
                                                            input.toLowerCase(),
                                                          );
                                                      }}
                                                    />
                                                    <Field
                                                      name={`tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].name`}
                                                      placeholder="Exercise name"
                                                      className="border p-1 rounded w-full h-10"
                                                    />
                                                    <Field
                                                      name={`tracks[${trackIndex}].days[${dayIndex}].${section}[${exIndex}].description`}
                                                      placeholder="Description"
                                                      className="border p-1 rounded w-full h-10"
                                                    />
                                                  </div>
                                                  {exerciseNameError && (
                                                    <div className="text-red-500 text-xs mt-1">
                                                      {exerciseNameError}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}

                                            {/* Buttons to add exercises or multisets */}
                                            <div className="flex gap-2 mt-2">
                                              <button
                                                type="button"
                                                className="text-blue-500"
                                                onClick={() =>
                                                  pushEx({
                                                    name: "",
                                                    description: "",
                                                  })
                                                }
                                              >
                                                Add Exercise
                                              </button>
                                              <button
                                                type="button"
                                                className="text-gray-500"
                                                onClick={() =>
                                                  pushEx({
                                                    exercises: [
                                                      {
                                                        name: "",
                                                        description: "",
                                                      },
                                                    ],
                                                    type: "dropset",
                                                    restTimeSec: 90,
                                                  })
                                                }
                                              >
                                                Add Dropset
                                              </button>
                                              <button
                                                type="button"
                                                className="text-purple-500"
                                                onClick={() =>
                                                  pushEx({
                                                    exercises: [
                                                      {
                                                        name: "",
                                                        description: "",
                                                      },
                                                      {
                                                        name: "",
                                                        description: "",
                                                      },
                                                    ],
                                                    type: "superset",
                                                    restTimeSec: 60,
                                                  })
                                                }
                                              >
                                                Add Superset
                                              </button>
                                              <button
                                                type="button"
                                                className="text-green-500"
                                                onClick={() =>
                                                  pushEx({
                                                    exercises: [
                                                      {
                                                        name: "",
                                                        description: "",
                                                      },
                                                      {
                                                        name: "",
                                                        description: "",
                                                      },
                                                      {
                                                        name: "",
                                                        description: "",
                                                      },
                                                    ],
                                                    type: "triset",
                                                    restTimeSec: 90,
                                                  })
                                                }
                                              >
                                                Add Tri-set
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </FieldArray>
                                    ))}

                                    <div>
                                      <label className="block font-medium mb-1">
                                        Tips
                                      </label>
                                      <ReactQuill
                                        value={day.tips}
                                        onChange={(content) =>
                                          setFieldValue(
                                            `tracks[${trackIndex}].days[${dayIndex}].tips`,
                                            content,
                                          )
                                        }
                                        className="bg-white border rounded"
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            {/* Add Day Button */}
                            {track.days.length < 7 && (
                              <button
                                type="button"
                                className="text-green-500"
                                onClick={() => {
                                  pushDay({
                                    dayNumber: track.days.length + 1,
                                    warmup: [],
                                    mainWorkout: [],
                                    cooldown: [],
                                    workoutFinisher: [],
                                    tips: "",
                                    title: "",
                                    duration: 0,
                                  });
                                  setTimeout(() => {
                                    setFieldTouched(
                                      `tracks[${trackIndex}].days[${track.days.length}]`,
                                      true,
                                      true,
                                    );
                                  });
                                }}
                              >
                                Add Day
                              </button>
                            )}
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  ))}
                </>
              )}
            </FieldArray>

            {/* Submit Buttons */}
            <button
              type="submit"
              className="mt-6 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              Update Program
            </button>
            <button
              type="button"
              className="mt-6 bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
              onClick={() => handleCreateAsNew(values)}
            >
              Save as New Program
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditWeekWorkoutProgram;
